/**
 * <custom-pack-flavor-picker>
 * Pack + flavor slots; intercepts theme <product-form> submit and POSTs JSON to cart/add.js.
 * Primabar: uses .product-info host, gallery image swap, cart:change / variant:add like theme.js.
 */
(() => {
  if (customElements.get('custom-pack-flavor-picker')) return;

  /** Preço riscado (compare-at) por box — único valor mockado por enquanto. */
  const NORMAL_COST_PER_BOX_USD = 27.93;

  /** Save % exibido nos pack tiles por modo de compra e tamanho do pack. */
  const PACK_SAVE_PCT = {
    subscription: { 1: 5, 2: 15, 4: 20, 5: 20 },
    onetime: { 1: 0, 2: 10, 4: 15, 5: 15 }
  };

  /** Badges flutuantes nos pack tiles (2 / 4 / 5 boxes) por modo de compra. */
  const PACK_BADGE = {
    subscription: {
      2: { text: '15% OFF', color: '#6b6b6b' },
      4: { text: '20% OFF + Ships Free', color: '#bc1218' },
      5: { text: '20% OFF + Ships Free', color: '#bc1218' }
    },
    onetime: {
      2: { text: '10% OFF', color: '#6b6b6b' },
      4: { text: '15% OFF + Ships Free', color: '#bc1218' },
      5: { text: '15% OFF + Ships Free', color: '#bc1218' }
    }
  };

  class CustomPackFlavorPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.packGrid = this.querySelector('[data-pack-grid]');
      this.packButtons = this.querySelectorAll('[data-pack-id]');
      this.flavorLabel = this.querySelector('[data-flavor-label]');
      this.singleView = this.querySelector('[data-flavor-single]');
      this.selectTrigger = this.querySelector('[data-select-trigger]');
      this.selectMenu = this.querySelector('[data-select-menu]');
      this.selectName = this.querySelector('[data-select-name]');
      this.selectThumb = this.querySelector('[data-select-thumb]');
      this.selectRows = this.querySelectorAll('[data-select-row]');
      this.listView = this.querySelector('[data-flavor-list]');
      this.flavorRows = this.querySelectorAll('[data-flavor-id]');
      this.placeholderText = this.selectName?.textContent?.trim() || 'Select a flavor';
      this.selectedFlavorId = '';
      this.builderPicked = this.querySelector('[data-builder-picked]');
      this.builderTotal = this.querySelector('[data-builder-total]');
      this.builderSuffix = this.querySelector('[data-builder-suffix]');
      this.builderProgress = this.querySelector('[data-builder-progress]');
      this.builderProgressBar = this.querySelector('[data-builder-progress-bar]');
      this.builderHelper = this.querySelector('[data-builder-helper]');
      this.builderHelperText = this.querySelector('[data-builder-helper-text]');
      this.builderActionAutofill = this.querySelector('[data-builder-action="autofill"]');
      this.builderActionClear = this.querySelector('[data-builder-action="clear"]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.submitSpinner = this.querySelector('[data-submit-spinner]');
      this.errorEl = this.querySelector('[data-error]');

      this.labelSingle =
        this._attr('flavor-label-single') || this.flavorLabel?.textContent?.trim() || 'CHOOSE FLAVOR';
      this.labelMulti =
        this._attr('flavor-label-multi') || this._readSectionSetting('flavorLabelMulti') || 'CHOOSE FLAVORS';
      this.errorTemplate =
        this._attr('error-incomplete') || 'Please complete your selection to continue.';

      this.helperEmpty = this.builderHelperText?.textContent?.trim() || 'Add boxes to complete your bundle.';
      this.helperActionAutofillLabel = this.builderActionAutofill?.textContent?.trim() || 'Auto-fill rest';
      this.helperActionClearLabel = this.builderActionClear?.textContent?.trim() || 'Clear';

      this.packSize = 1;
      this.quantities = new Map();
      this.defaultFlavorId = this.dataset.defaultFlavor || '';
      this.currentProductHandle = (this.dataset.currentProductHandle || '').trim();
      this.originalProductTitle = (this.dataset.productTitleOriginal || '').trim();
      this.bundleTitleSuffix = (this.dataset.bundleTitleSuffix || 'Bundle').trim() || 'Bundle';
      this.varietyPackUrl = (this.dataset.varietyPackUrl || '').trim();
      this.varietyPackHandle = (this.dataset.varietyPackHandle || '').trim();

      // ---- Buy 5 Get 1 Free (B1G1) ----
      // Entirely opt-in: data-b1g1 is only emitted on the promo template, so
      // every other PDP keeps this.b1g1 = false and runs the original code path.
      this.b1g1 = this.dataset.b1g1 === 'true';
      this.giftCount = Math.max(0, parseInt(this.dataset.giftCount, 10) || 0);
      // Whether the *currently selected* pack is the free-gift pack (independent
      // of purchase mode). freeGiftActive additionally requires subscription.
      this.currentPackFreeGift = false;
      this.freeGiftActive = false;
      // One-time total (and strike) for the 6 paid boxes — set directly so it
      // matches the real amount the discount app charges (not derived per-bar).
      this.onetimeTotal = parseFloat(this.dataset.onetimeTotal) || 0;
      this.onetimeCompare = parseFloat(this.dataset.onetimeCompare) || 0;
      this.varietyRow = this.b1g1
        ? this.querySelector('.cpfp__flavor-row--variety[data-flavor-id]')
        : null;
      this.b1g1Banner = this.b1g1 ? this.querySelector('[data-b1g1-banner]') : null;
      this.b1g1Footnote = this.b1g1 ? this.querySelector('[data-b1g1-footnote]') : null;

      // Track which product's nutrition modal is currently rendered, and
      // snapshot the initial server-rendered modal so we can restore it
      // cheaply when the user returns to the original PDP product.
      this._modalHandle = this.currentProductHandle || '';
      this._pendingModalHandle = null;
      const initialTrigger = document.getElementById('nutritionModalTrigger');
      const initialModal = document.getElementById('nutritionModal');
      if (initialTrigger && initialModal) {
        this._initialModalSnapshot = {
          triggerHtml: initialTrigger.outerHTML,
          modalHtml: initialModal.outerHTML,
        };
      }

      this._rcBaseSub = null;
      this._rcBaseOtp = null;
      this._rcUpdatingFromUs = false;

      this.packButtons.forEach((btn) => {
        const priceEl = btn.querySelector('.cpfp__pack-price');
        if (priceEl) {
          const clone = priceEl.cloneNode(true);
          clone.querySelector('.cpfp__pack-save-sep')?.remove();
          clone.querySelector('.cpfp__pack-save')?.remove();
          btn.dataset.packPriceText = clone.textContent.trim();
        }
      });

      this._bindEvents();
      this._wireProductForm();

      const initialPack =
        this.querySelector('[data-pack-id].is-selected') || this.querySelector('[data-pack-id]');
      if (initialPack) this._selectPack(initialPack, { initial: true });

      this._wireRechargeWidget();
      this._syncPackPricing();
      this._startSaveLabelPolling();
    }

    /** Re-aplica Save % até o widget Recharge montar e em cada troca Subscribe/OTP. */
    _startSaveLabelPolling() {
      if (this._savePollId) clearInterval(this._savePollId);
      let ticks = 0;
      this._savePollId = setInterval(() => {
        this._syncPackPricing();
        ticks += 1;
        if (ticks >= 16) {
          clearInterval(this._savePollId);
          this._savePollId = null;
        }
      }, 400);
    }

    _getProductTitleNode() {
      const host =
        this.closest('safe-sticky.product-info') ||
        this.closest('.product-info') ||
        this.closest('product-info');
      if (!host) return null;
      const wrap = host.querySelector('.product-info__block-item[data-block-type="title"]');
      return wrap?.querySelector('.product-title') || null;
    }

    /** Substitui o segmento após o último " - " pelo sufixo (nome do sabor ou sufixo de bundle). */
    _replaceProductTitleSuffix(suffix) {
      const raw = this.originalProductTitle || '';
      if (!suffix) return raw;

      const sep = ' - ';
      const idx = raw.lastIndexOf(sep);
      if (idx !== -1) return raw.slice(0, idx + sep.length) + suffix;
      return raw ? raw + sep + suffix : suffix;
    }

    _syncProductTitleDisplay(flavorLabel) {
      const node = this._getProductTitleNode();
      if (!node || !this.originalProductTitle) return;

      if (this.packSize > 1) {
        node.textContent = this._replaceProductTitleSuffix(this.bundleTitleSuffix);
        return;
      }

      if (!flavorLabel) {
        node.textContent = this.originalProductTitle;
        return;
      }

      node.textContent = this._replaceProductTitleSuffix(flavorLabel);
    }

    _applyDefaultSingleFlavor() {
      // Prefer the row that matches the CURRENT PDP product, so landing on
      // /products/<flavor> always starts with that flavor selected — never the
      // hardcoded default flavor (which would otherwise force a section
      // refresh back to the default product on every page load).
      let row = null;
      if (this.currentProductHandle) {
        row = this.querySelector(
          `[data-select-row][data-product-handle="${CSS.escape(this.currentProductHandle)}"]`
        );
        if (row && row.getAttribute('aria-disabled') === 'true') row = null;
      }

      if (!row && this.defaultFlavorId) {
        row = this.querySelector(
          `[data-select-row="${CSS.escape(this.defaultFlavorId)}"]`
        );
        if (row && row.getAttribute('aria-disabled') === 'true') row = null;
      }

      if (!row) return false;

      this._selectFlavor(row, { initial: true });
      this._closeSelect();
      return true;
    }

    disconnectedCallback() {
      if (this.productFormEl && this._onFormSubmit) {
        this.productFormEl.removeEventListener('submit', this._onFormSubmit, { capture: true });
      }
      if (this._onDocClick) {
        document.removeEventListener('click', this._onDocClick);
      }
      if (this._rcObservers) {
        this._rcObservers.forEach((o) => o.disconnect());
        this._rcObservers = [];
      }
      if (this._galleryAbortController) {
        try { this._galleryAbortController.abort(); } catch (_) {}
        this._galleryAbortController = null;
      }
      if (this._rcDocObserver) {
        this._rcDocObserver.disconnect();
        this._rcDocObserver = null;
      }
      if (this._onRcOptionChange) {
        document.removeEventListener('change', this._onRcOptionChange, true);
      }
      if (this._savePollId) {
        clearInterval(this._savePollId);
        this._savePollId = null;
      }
    }

    _cartRoot() {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
      return '/';
    }

    _wireProductForm() {
      if (this.submitBtn) return;

      const host =
        this.closest('product-info') ||
        this.closest('.product-info') ||
        this.closest('.shopify-section') ||
        document;
      const form = host.querySelector('product-form form[action*="/cart/add"]');
      if (!form) return;

      this.productFormEl = form;
      this.productFormHost = form.closest('product-form');
      this.productFormSubmit = this.productFormHost?.querySelector('[type="submit"]');
      this.productFormSpinner = this.productFormHost?.querySelector('.loading__spinner');

      this._onFormSubmit = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this._clearError();

        if (this._totalQty() !== this._effectiveTotal()) {
          this._showError(this.errorTemplate);
          return;
        }

        const items = this._buildCartItems();
        this._applySellingPlansToItems(items);
        if (!items.length) {
          this._showError(this.errorTemplate);
          return;
        }

        this._addToCart(items);
      };

      form.addEventListener('submit', this._onFormSubmit, { capture: true });
    }

    _attr(name) {
      return this.getAttribute(`data-${name}`) || '';
    }

    _readSectionSetting() {
      return '';
    }

    _getSelectedSellingPlanId() {
      const form = this.productFormEl;
      if (!form) return null;

      try {
        const fd = new FormData(form);
        const keys = ['selling_plan', 'rc_selling_plan', 'selling_plan_id'];
        for (let i = 0; i < keys.length; i++) {
          const raw = fd.get(keys[i]);
          if (raw == null) continue;
          const value = String(raw).trim();
          if (!value) continue;
          const num = Number(value);
          if (Number.isFinite(num) && num > 0) return num;
        }
        return null;
      } catch {
        return null;
      }
    }

    _subscriptionModeActive() {
      if (this._getSelectedSellingPlanId()) return true;
      const form = this.productFormEl;
      if (!form) return false;
      if (form.querySelector('input[name="purchaseOption"][value="subscription"]:checked')) return true;
      if (form.querySelector('input[name="purchase_type"][value="subscription"]:checked')) return true;
      return false;
    }

    /**
     * Planos vêm do Liquid (`data-selling-plan-ids` por sabor). O ID do formulário
     * é da variante da PDP; cada outro sabor tem os seus próprios selling_plan ids.
     */
    _sellingPlanIdsForFlavor(flavorId) {
      const el =
        this.querySelector(`[data-select-row="${CSS.escape(flavorId)}"][data-selling-plan-ids]`) ||
        this.querySelector(`[data-flavor-id="${CSS.escape(flavorId)}"][data-selling-plan-ids]`);
      const raw = el?.getAttribute?.('data-selling-plan-ids') || '';
      return raw
        .split(',')
        .map((s) => Number(String(s).trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    }

    _sellingPlanForFlavorRow(flavorId, formPlanId, subMode) {
      const ids = this._sellingPlanIdsForFlavor(flavorId);
      if (!ids.length) return null;

      if (formPlanId) {
        const fp = Number(formPlanId);
        if (ids.includes(fp)) return fp;
        const rc = this.productFormEl?.querySelector('select[name="rc_selling_plan"]');
        const rcv = rc?.value ? Number(rc.value) : 0;
        if (rcv && ids.includes(rcv)) return rcv;
      }

      if (subMode) return ids[0] || null;
      return null;
    }

    _applySellingPlansToItems(items) {
      if (!items?.length) return;
      const formPlan = this._getSelectedSellingPlanId();
      const subMode = this._subscriptionModeActive();

      items.forEach((item) => {
        const flavorId = item._flavorId;
        delete item._flavorId;
        if (!flavorId) return;
        const plan = this._sellingPlanForFlavorRow(flavorId, formPlan, subMode);
        if (plan) item.selling_plan = plan;
      });
    }

    _bindEvents() {
      this.packButtons.forEach((btn) => {
        btn.addEventListener('click', () => this._selectPack(btn));
      });

      this.selectTrigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleSelect();
      });

      this.selectTrigger?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          this._openSelect();
        } else if (e.key === 'Escape') {
          this._closeSelect();
        }
      });

      this.selectRows.forEach((row) => {
        row.addEventListener('click', (e) => {
          e.stopPropagation();
          if (row.getAttribute('aria-disabled') === 'true') return;
          this._selectFlavor(row);
        });
      });

      this._onDocClick = (e) => {
        if (!this.selectMenu || this.selectMenu.hidden) return;
        if (!this.contains(e.target)) this._closeSelect();
      };
      document.addEventListener('click', this._onDocClick);

      this.flavorRows.forEach((row) => {
        row.querySelectorAll('[data-step]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = parseInt(btn.dataset.step, 10) || 0;
            this._changeQty(row, delta);
          });
        });
      });

      this.builderActionAutofill?.addEventListener('click', () => this._autofillRemaining());
      this.builderActionClear?.addEventListener('click', () => this._clearAll());

      this.submitBtn?.addEventListener('click', () => this._onSubmit());
    }

    _openSelect() {
      if (!this.selectMenu || !this.selectTrigger) return;
      this.selectMenu.hidden = false;
      this.selectTrigger.setAttribute('aria-expanded', 'true');
    }

    _closeSelect() {
      if (!this.selectMenu || !this.selectTrigger) return;
      this.selectMenu.hidden = true;
      this.selectTrigger.setAttribute('aria-expanded', 'false');
    }

    _toggleSelect() {
      if (!this.selectMenu) return;
      this.selectMenu.hidden ? this._openSelect() : this._closeSelect();
    }

    _selectFlavor(row, { initial = false } = {}) {
      const flavorId = row.dataset.selectRow;
      if (!flavorId) return;

      if (!initial) {
        this._rcBaseSub = null;
        this._rcBaseOtp = null;
      }

      this.quantities.clear();
      this.quantities.set(flavorId, 1);
      this.selectedFlavorId = flavorId;

      const label = row.dataset.flavorLabel || row.querySelector('.cpfp__select-row-name')?.textContent?.trim() || '';
      if (this.selectName) this.selectName.textContent = label;

      this._updateSelectThumb(row);
      this._updateFeaturedImage(row);

      this.selectRows.forEach((r) => {
        r.setAttribute('aria-selected', r === row ? 'true' : 'false');
      });

      this._closeSelect();
      this._clearError();
      this._updateSubmitState();
      this._syncProductTitleDisplay(label);
      if (!initial) this._refreshSectionForFlavor(row);
      this._reconcileNutritionModal();
    }

    /**
     * Boxes the builder targets. The B1G1 pack is always a 6-box bundle (pack
     * size + gift), in BOTH subscription and one-time — the only difference is
     * whether 1 box is free (subscription) or all 6 are paid (one-time). Other
     * packs just use packSize, so the original behavior is unchanged.
     */
    _effectiveTotal() {
      return this.packSize + (this.currentPackFreeGift ? this.giftCount : 0);
    }

    _isFreeGiftRow(row) {
      return this.freeGiftActive && this.varietyRow && row === this.varietyRow;
    }

    /** Pre-fill the Variety Pack row with the free gift quantity (minimum). */
    _applyFreeGift() {
      if (!this.freeGiftActive || !this.varietyRow) return;
      this._setRowQty(this.varietyRow, this.giftCount);
    }

    /**
     * Selecting the B1G1 pack starts the bundle pre-filled: 1 of every
     * available flavor (Variety Pack first so it keeps its free-gift minimum),
     * up to the box cap — so a complete bundle needs zero extra clicks. The
     * shopper can still adjust any row afterwards.
     */
    _prefillFreeGiftBundle() {
      if (!this.currentPackFreeGift || !this.varietyRow) return;
      const cap = this._effectiveTotal();

      this._setRowQty(this.varietyRow, this.giftCount);
      let total = this._totalQty();

      this.flavorRows.forEach((row) => {
        if (total >= cap) return;
        if (row === this.varietyRow) return;
        if (row.classList.contains('is-unavailable')) return;
        if ((this.quantities.get(row.dataset.flavorId) || 0) > 0) return;
        this._setRowQty(row, 1);
        total += 1;
      });
    }

    /** Show the promo banner/footnote only while the free gift is active. */
    _updateB1g1Visibility() {
      if (!this.b1g1) return;
      this.classList.toggle('is-free-gift-active', !!this.freeGiftActive);
      if (this.b1g1Banner) this.b1g1Banner.hidden = !this.freeGiftActive;
      if (this.b1g1Footnote) this.b1g1Footnote.hidden = !this.freeGiftActive;
    }

    _isSubscriptionSelected() {
      return this._getPurchaseMode() === 'subscription';
    }

    /**
     * The free gift only applies to SUBSCRIPTION orders (the gift app only adds
     * it for subscriptions). So it requires the free-gift pack selected AND
     * subscription mode. One-time → false → the pack behaves as a standard
     * multi-pack (pay for all slots, no forced Variety minimum, no offset).
     */
    _desiredFreeGiftActive() {
      return !!(
        this.b1g1 &&
        this.currentPackFreeGift &&
        this.packSize > 1 &&
        this.giftCount > 0 &&
        this.varietyRow &&
        this._isSubscriptionSelected()
      );
    }

    /**
     * Re-evaluate free-gift mode when the shopper toggles Subscribe ↔ One-time
     * while the B1G1 pack is selected. The box count stays 6 either way; only
     * the free gift (offset + Variety minimum + promo messaging) changes. We
     * re-prefill a fresh 6/6 bundle so the Variety minimum is correct for the
     * new mode.
     */
    _syncFreeGiftMode() {
      if (!this.b1g1 || !this.currentPackFreeGift) return;
      const desired = this._desiredFreeGiftActive();
      if (desired === this.freeGiftActive) return;

      this.freeGiftActive = desired;
      this._updateB1g1Visibility();

      if (this.listView && !this.listView.hidden) {
        this.quantities.clear();
        this.flavorRows.forEach((row) => this._setRowQty(row, 0));
        this._prefillFreeGiftBundle();
        this._refreshStepperDisabledState();
        this._refreshBuilderUi();
        this._updateSubmitState();
      }
    }

    _autofillRemaining() {
      const total = this._effectiveTotal();
      let picked = this._totalQty();
      if (picked >= total) return;

      const available = Array.from(this.flavorRows).filter(
        (row) => !row.classList.contains('is-unavailable') && !this._isFreeGiftRow(row)
      );
      if (!available.length) return;

      let i = 0;
      while (picked < total) {
        const row = available[i % available.length];
        const flavorId = row.dataset.flavorId;
        const cur = this.quantities.get(flavorId) || 0;
        this.quantities.set(flavorId, cur + 1);
        this._setRowQty(row, cur + 1);
        picked += 1;
        i += 1;
      }
      this._refreshStepperDisabledState();
      this._refreshBuilderUi();
      this._clearError();
      this._updateSubmitState();
    }

    _clearAll() {
      this.flavorRows.forEach((row) => this._setRowQty(row, 0));
      if (this.freeGiftActive) this._applyFreeGift();
      this._refreshStepperDisabledState();
      this._refreshBuilderUi();
      this._clearError();
      this._updateSubmitState();
    }

    _selectPack(btn, { initial = false } = {}) {
      const prevPackSize = this.packSize;
      const size = Math.max(1, parseInt(btn.dataset.packSize, 10) || 1);

      this.packButtons.forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-selected', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      this.packSize = size;
      this.currentPackFreeGift = !!(this.b1g1 && size > 1 && btn.dataset.packFreeGift);
      this.freeGiftActive = this._desiredFreeGiftActive();
      this._updateB1g1Visibility();
      this.quantities.clear();
      this._clearError();

      if (size === 1) {
        const fromMultiPack = !initial && prevPackSize !== 1;
        this._renderSingleView({ initial, fromMultiPack });
      } else {
        this._renderMultiView();
        if (this.currentPackFreeGift) {
          this._prefillFreeGiftBundle();
          this._refreshStepperDisabledState();
          this._refreshBuilderUi();
        }
      }

      this._updateSubmitState();
      this._syncPackPricing();
      this._reconcileNutritionModal();
    }

    _renderSingleView({ initial = false, fromMultiPack = false } = {}) {
      if (this.flavorLabel) this.flavorLabel.textContent = this.labelSingle;
      if (this.singleView) this.singleView.hidden = false;
      if (this.listView) this.listView.hidden = true;
      this._closeSelect();

      if ((initial || fromMultiPack) && this._applyDefaultSingleFlavor()) {
        return;
      }

      this.selectedFlavorId = '';
      if (this.selectName) this.selectName.textContent = this.placeholderText;
      this._updateSelectThumb(null);
      this.selectRows.forEach((r) => r.setAttribute('aria-selected', 'false'));
      this._syncProductTitleDisplay('');
    }

    _updateSelectThumb(el) {
      if (!this.selectThumb) return;
      const src = el?.dataset?.thumbImage || el?.dataset?.productImage;
      if (!src) {
        this.selectThumb.innerHTML = '';
        return;
      }
      const alt = el?.dataset?.thumbAlt || el?.dataset?.productImageAlt || '';
      const safeAlt = String(alt).replace(/"/g, '&quot;');
      this.selectThumb.innerHTML = `<img src="${src}" alt="${safeAlt}" width="40" height="40" loading="lazy">`;
    }

    _updateFeaturedImage(option) {
      const src = option?.dataset?.productImage;
      if (!src) return;

      const initialSlide = document.querySelector('.product-gallery__media.is-initial');
      let img = initialSlide?.querySelector('img');
      if (!img) {
        const firstVisible = document.querySelector(
          '.product-gallery__carousel .product-gallery__media:not([hidden]) img'
        );
        img = firstVisible || null;
      }
      if (!img) return;

      const srcset = option.dataset.productImageSrcset || '';
      const alt = option.dataset.productImageAlt || '';
      const width = option.dataset.productImageWidth;
      const height = option.dataset.productImageHeight;

      img.removeAttribute('srcset');
      img.src = src;
      if (srcset) img.srcset = srcset;
      img.alt = alt;
      if (width) img.width = Number(width);
      if (height) img.height = Number(height);
    }

    _renderMultiView() {
      if (this.flavorLabel) this.flavorLabel.textContent = this.labelMulti;
      if (this.singleView) this.singleView.hidden = true;
      if (this.listView) this.listView.hidden = false;

      this._syncProductTitleDisplay('');

      this.flavorRows.forEach((row) => {
        this._setRowQty(row, 0);
      });
      this._refreshStepperDisabledState();
      this._refreshBuilderUi();
    }

    _changeQty(row, delta) {
      if (row.classList.contains('is-unavailable')) return;

      const flavorId = row.dataset.flavorId;
      const current = this.quantities.get(flavorId) || 0;
      const total = this._totalQty();
      const cap = this._effectiveTotal();
      // The free gift row can hold extra (paid) variety packs, but never drops
      // below the free gift quantity while the B1G1 pack is selected.
      const floor = this._isFreeGiftRow(row) ? this.giftCount : 0;
      let next = current + delta;

      if (next < floor) next = floor;
      if (delta > 0 && total >= cap) return;
      if (next > cap) next = cap;

      this.quantities.set(flavorId, next);
      this._setRowQty(row, next);
      this._refreshStepperDisabledState();
      this._refreshBuilderUi();
      this._clearError();
      this._updateSubmitState();
    }

    _setRowQty(row, qty) {
      const qtyEl = row.querySelector('[data-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
      row.classList.toggle('has-qty', qty > 0);

      const flavorId = row.dataset.flavorId;
      if (qty > 0) this.quantities.set(flavorId, qty);
      else this.quantities.delete(flavorId);
    }

    _refreshBuilderUi() {
      if (!this.listView || this.listView.hidden) return;

      const total = this._effectiveTotal();
      const picked = this._totalQty();
      const remaining = Math.max(0, total - picked);
      const full = picked === total && total > 0;
      const pct = total > 0 ? Math.min(100, (picked / total) * 100) : 0;

      if (this.builderPicked) this.builderPicked.textContent = String(picked);
      if (this.builderTotal) this.builderTotal.textContent = String(total);
      if (this.builderSuffix) {
        this.builderSuffix.textContent = total === 1 ? ' box' : ' boxes';
      }

      if (this.builderProgress) {
        this.builderProgress.classList.toggle('is-full', full);
      }
      if (this.builderProgressBar) {
        this.builderProgressBar.style.width = pct + '%';
      }

      if (this.builderHelper) {
        this.builderHelper.classList.toggle('is-warn', !full && picked > 0 && picked < total);
        this.builderHelper.classList.toggle('is-ok', full);
      }

      if (this.builderHelperText) {
        if (full) {
          this.builderHelperText.textContent = `✓ Bundle complete · ${total} ${total === 1 ? 'box' : 'boxes'}`;
        } else if (picked === 0) {
          this.builderHelperText.textContent = this.helperEmpty;
        } else {
          this.builderHelperText.textContent =
            remaining === 1
              ? 'Add 1 more box to complete your bundle.'
              : `Add ${remaining} more boxes to complete your bundle.`;
        }
      }

      if (this.builderActionAutofill) {
        this.builderActionAutofill.hidden = !(picked > 0 && !full);
      }
      if (this.builderActionClear) {
        this.builderActionClear.hidden = !(picked > 0);
      }
    }

    _refreshStepperDisabledState() {
      const total = this._totalQty();
      const atCap = total >= this._effectiveTotal();

      this.flavorRows.forEach((row) => {
        const flavorId = row.dataset.flavorId;
        const qty = this.quantities.get(flavorId) || 0;
        const unavailable = row.classList.contains('is-unavailable');

        const minus = row.querySelector('.cpfp__step--minus');
        const plus = row.querySelector('.cpfp__step--plus');

        // Free gift row: extra (paid) variety packs allowed, but can't go below
        // the free gift quantity.
        if (this._isFreeGiftRow(row)) {
          if (minus) minus.disabled = qty <= this.giftCount;
          if (plus) plus.disabled = unavailable || atCap;
          return;
        }

        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = unavailable || atCap;
      });
    }

    _totalQty() {
      let sum = 0;
      this.quantities.forEach((q) => {
        sum += q;
      });
      return sum;
    }

    _updateSubmitState() {
      if (!this.submitBtn) return;
      const complete = this._totalQty() === this._effectiveTotal();
      this.submitBtn.classList.toggle('is-ready', complete);
    }

    _showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    _onSubmit() {
      if (this.submitBtn.dataset.loading === 'true') return;

      const total = this._totalQty();
      if (total !== this._effectiveTotal()) {
        this._showError(this.errorTemplate);
        return;
      }

      const items = this._buildCartItems();
      this._applySellingPlansToItems(items);
      if (!items.length) {
        this._showError('Please choose a flavor.');
        return;
      }

      this._addToCart(items);
    }

    _buildCartItems() {
      const items = [];
      // The free Variety Pack is auto-added to the cart by the gift app, so the
      // builder must NOT also add it — otherwise the cart ends up with 7 items
      // instead of 6. The Variety row still *displays* its full quantity (so the
      // bundle reads 6/6), but we send one fewer (the free gift): display 1 → 0,
      // display 2 → 1, etc. All other flavors send their displayed quantity.
      const giftFlavorId =
        this.freeGiftActive && this.varietyRow ? this.varietyRow.dataset.flavorId : null;

      this.quantities.forEach((qty, flavorId) => {
        let cartQty = qty;
        if (giftFlavorId && flavorId === giftFlavorId) {
          cartQty = qty - this.giftCount;
        }
        if (cartQty <= 0) return;
        const variantId = this._variantIdFor(flavorId);
        if (!variantId) return;

        items.push({ id: Number(variantId), quantity: cartQty, _flavorId: flavorId });
      });
      return items;
    }

    _variantIdFor(flavorId) {
      const el =
        this.querySelector(`[data-select-row="${CSS.escape(flavorId)}"][data-variant-id]`) ||
        this.querySelector(`[data-flavor-id="${CSS.escape(flavorId)}"][data-variant-id]`);
      return el?.dataset?.variantId || '';
    }

    async _addToCart(items) {
      this._setLoading(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const root = this._cartRoot();
      const addUrl = `${root}cart/add.js`;

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const body = { items };
      if (sectionsToBundle.length) {
        body.sections = sectionsToBundle.join(',');
        body.sections_url = window.location.pathname;
      }

      try {
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });

        const data = await res.json();

        if (!res.ok || data.status) {
          const message = data.description || data.message || 'Could not add to cart.';
          this._showError(message);
          this._setLoading(false);
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          return;
        }

        const cartRes = await fetch(`${root}cart.js`);
        const cartContent = await cartRes.json();
        if (data.sections) cartContent.sections = data.sections;

        const lineItems = data.items ? data.items : [data];
        const onSuccessDo = this.productFormEl
          ? new FormData(this.productFormEl).get('on_success')
          : null;

        this.productFormEl?.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: lineItems,
              cart: cartContent,
              onSuccessDo
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo,
              cart: cartContent
            }
          })
        );

        this._resetSelection();
      } catch (err) {
        console.error('[custom-pack-flavor-picker] add-to-cart failed', err);
        this._showError('Something went wrong. Please try again.');
      } finally {
        this._setLoading(false);
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    _setLoading(isLoading) {
      if (this.submitBtn) {
        this.submitBtn.dataset.loading = isLoading ? 'true' : 'false';
        this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.submitBtn.disabled = isLoading;
        if (this.submitSpinner) this.submitSpinner.hidden = !isLoading;
        return;
      }

      if (this.productFormSubmit) {
        if (isLoading) {
          this.productFormSubmit.setAttribute('aria-busy', 'true');
          this.productFormSubmit.setAttribute('aria-disabled', 'true');
        } else {
          this.productFormSubmit.removeAttribute('aria-busy');
          this.productFormSubmit.removeAttribute('aria-disabled');
        }
      }
      if (this.productFormSpinner) {
        this.productFormSpinner.classList.toggle('hidden', !isLoading);
      }
    }

    /* ------------------------------------------------------------------
     * Recharge subscription widget — multi-box pricing override
     * Shows: <s>$base × packSize</s>  <strong>$final</strong>
     *   - Subscribe & save  → final = base_sub × packSize
     *   - One-time          → final = base_otp × packSize × (1 - pack_discount)
     * On packSize === 1 we restore Recharge's native rendering.
     * ----------------------------------------------------------------*/
    _wireRechargeWidget() {
      this._rcOverrideRoot = document; // Recharge mounts anywhere; search globally + shadow DOM.

      this._onRcOptionChange = (e) => {
        const path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target];
        const hit = path.some((el) => this._isRechargePurchaseTarget(el));
        if (!hit) return;
        this._schedulePriceRefresh();
      };
      document.addEventListener('change', this._onRcOptionChange, true);
      document.addEventListener('click', this._onRcOptionChange, true);

      this._onRcPurchaseInteraction = () => {
        this._schedulePriceRefresh();
      };

      this._bindRechargePurchaseInteraction = () => {
        const sr = this._getRechargeShadowRoot();
        if (!sr || sr._cpfpPurchaseBind) return;
        sr._cpfpPurchaseBind = true;
        sr.addEventListener('click', this._onRcPurchaseInteraction, true);
        sr.addEventListener('change', this._onRcPurchaseInteraction, true);
      };

      this._rcObservers = [];
      this._rcObservedRoots = new Set();
      this._rcScheduled = false;

      // First-paint kick: Recharge widget often mounts after our element.
      let tries = 0;
      const tick = () => {
        const { subLabel, otpLabel } = this._findRechargeOptions();
        if (subLabel || otpLabel) {
          this._attachRechargeObservers(subLabel || otpLabel);
          this._bindRechargePurchaseInteraction();
          if (this.packSize <= 1) this._storeRechargeBasePrices();
          this._syncPackPricing();
          return;
        }
        if (tries++ < 80) setTimeout(tick, 250);
      };
      tick();

      // Also keep observing document for late-mounted shadow hosts.
      this._rcDocObserver = new MutationObserver(() => {
        if (this._rcUpdatingFromUs) return;
        if (this._rcScheduled) return;
        this._rcScheduled = true;
        requestAnimationFrame(() => {
          this._rcScheduled = false;
          const { subLabel, otpLabel } = this._findRechargeOptions();
          if (!subLabel && !otpLabel) return;
          this._attachRechargeObservers(subLabel || otpLabel);
          this._bindRechargePurchaseInteraction();
          if (this.packSize <= 1) {
            this._storeRechargeBasePrices();
          }
          this._syncPackPricing();
        });
      });
      this._rcDocObserver.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
      });
    }

    /**
     * Recursive querySelector that pierces every shadow root reachable from `root`.
     * Returns the first element matching `selector` or null.
     */
    _deepQuery(selector, root) {
      root = root || document;
      const direct = root.querySelector ? root.querySelector(selector) : null;
      if (direct) return direct;
      const elements = root.querySelectorAll ? root.querySelectorAll('*') : [];
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];
        if (el.shadowRoot) {
          const found = this._deepQuery(selector, el.shadowRoot);
          if (found) return found;
        }
      }
      return null;
    }

    _findRechargeOptions() {
      const subInput = this._deepQuery(
        'input[name="purchaseOption"][value="subscription"]'
      );
      const otpInput = this._deepQuery(
        'input[name="purchaseOption"][value="onetime"]'
      );
      const sub = subInput ? subInput.closest('.rc-purchase-option__label') : null;
      const otp = otpInput ? otpInput.closest('.rc-purchase-option__label') : null;
      return { subLabel: sub, otpLabel: otp };
    }

    /**
     * Observe the shadow root (or document) that actually contains the Recharge
     * labels, so we re-apply when Recharge re-renders.
     */
    _attachRechargeObservers(referenceEl) {
      if (!referenceEl) return;
      const targets = [];

      // The root containing `referenceEl` (a ShadowRoot or document).
      const ownerRoot = referenceEl.getRootNode ? referenceEl.getRootNode() : null;
      if (ownerRoot && ownerRoot !== document) targets.push(ownerRoot);

      // Plus the .rc-widget host (light DOM) if present, in case price text is
      // mutated by Recharge's outer scripts (e.g. for currency switchers).
      const widgetHost = this._deepQuery('.rc-widget');
      if (widgetHost) targets.push(widgetHost);

      targets.forEach((target) => {
        if (this._rcObservedRoots.has(target)) return;
        const obs = new MutationObserver(() => {
          if (this._rcUpdatingFromUs) return;
          if (this._rcScheduled) return;
          this._rcScheduled = true;
          requestAnimationFrame(() => {
            this._rcScheduled = false;
            if (this.packSize <= 1) {
              this._storeRechargeBasePrices();
            }
            this._syncPackPricing();
          });
        });
        obs.observe(target, {
          childList: true,
          subtree: true,
          characterData: true
        });
        this._rcObservers.push(obs);
        this._rcObservedRoots.add(target);
      });
    }

    _parsePrice(text) {
      if (!text) return null;
      const raw = String(text).replace(/\s/g, '');
      const m = raw.match(/^([^\d.,-]*)(-?[\d.,]+)([^\d.,]*)$/);
      if (!m) return null;
      const prefix = m[1] || '';
      const suffix = m[3] || '';
      let num = m[2];

      // Detect locale: if last "," is after last ".", it's likely a decimal comma.
      const hasComma = num.includes(',');
      const hasDot = num.includes('.');
      if (hasComma && hasDot) {
        if (num.lastIndexOf(',') > num.lastIndexOf('.')) {
          num = num.replace(/\./g, '').replace(',', '.');
        } else {
          num = num.replace(/,/g, '');
        }
      } else if (hasComma) {
        // Treat as decimal if exactly 1 comma with 1–2 trailing digits.
        const parts = num.split(',');
        if (parts.length === 2 && parts[1].length <= 2) {
          num = parts[0] + '.' + parts[1];
        } else {
          num = num.replace(/,/g, '');
        }
      }

      const value = parseFloat(num);
      if (!Number.isFinite(value)) return null;
      return { prefix, suffix, value };
    }

    _formatPrice(parsed, value) {
      const prefix = parsed?.prefix ?? '$';
      const suffix = parsed?.suffix ?? '';
      const formatted = value.toFixed(2);
      return `${prefix}${formatted}${suffix}`;
    }

    _readPriceFromLabel(label, kind) {
      if (!label) return null;
      const candidates = Array.from(label.querySelectorAll('.rc-price')).filter(
        (el) =>
          !el.closest('[data-cpfp-rc]') &&
          !el.hasAttribute('data-cpfp-rc-hidden') &&
          el.style.display !== 'none'
      );
      if (!candidates.length) return null;

      let preferred = null;
      if (kind === 'sub') {
        preferred = candidates.find((el) =>
          (el.getAttribute('part') || '').includes('discounted-price')
        );
        if (!preferred) preferred = candidates[candidates.length - 1];
      } else {
        // OTP — avoid any element flagged as a "discounted" sibling.
        preferred = candidates.find(
          (el) => !((el.getAttribute('part') || '').includes('discounted-price'))
        );
        if (!preferred) preferred = candidates[0];
      }
      return this._parsePrice(preferred.textContent);
    }

    _ensureRechargeBasePrices() {
      const { subLabel, otpLabel } = this._findRechargeOptions();
      if (!subLabel && !otpLabel) return false;

      if (!this._rcBaseSub || !this._rcBaseOtp) {
        const sub = !subLabel?.classList.contains('cpfp-rc-overridden')
          ? this._readPriceFromLabel(subLabel, 'sub')
          : this._rcBaseSub;
        const otp = !otpLabel?.classList.contains('cpfp-rc-overridden')
          ? this._readPriceFromLabel(otpLabel, 'otp')
          : this._rcBaseOtp;
        if (sub) this._rcBaseSub = sub;
        if (otp) this._rcBaseOtp = otp;
      }

      return !!(this._rcBaseSub && this._rcBaseOtp);
    }

    _resolveVariantPriceCents() {
      const form = this.productFormEl;
      const idInput = form?.querySelector('input[name="id"]');
      const variantId = idInput?.value;
      if (!variantId) return null;

      if (window.Shopify?.products) {
        const variant = window.Shopify.products
          .flatMap((p) => p.variants || [])
          .find((v) => v.id.toString() === variantId.toString());
        if (variant?.price) return variant.price;
      }

      const scripts = document.querySelectorAll('script[type="application/json"][data-product-json]');
      for (let i = 0; i < scripts.length; i++) {
        try {
          const data = JSON.parse(scripts[i].textContent);
          const variant = data.variants?.find((v) => v.id.toString() === variantId.toString());
          if (variant?.price) return variant.price;
        } catch {
          /* continue */
        }
      }

      const metaPrice = window.Shopify?.analytics?.meta?.selectedVariant?.price;
      return metaPrice || null;
    }

    _getOtpPerBoxCents() {
      const { otpLabel } = this._findRechargeOptions();
      if (otpLabel && !otpLabel.classList.contains('cpfp-rc-overridden')) {
        const parsed = this._readPriceFromLabel(otpLabel, 'otp');
        if (parsed) return Math.round(parsed.value * 100);
      }
      if (this._rcBaseOtp) return Math.round(this._rcBaseOtp.value * 100);
      return this._resolveVariantPriceCents();
    }

    _inferSubPct(otpPerBoxCents) {
      if (!otpPerBoxCents) return 0;
      if (!this._rcBaseSub) this._ensureRechargeBasePrices();
      if (!this._rcBaseSub) return 0;

      const subVisibleCents = Math.round(this._rcBaseSub.value * 100);
      if (subVisibleCents <= 0 || subVisibleCents > otpPerBoxCents) return 0;

      return Math.max(0, Math.round((1 - subVisibleCents / otpPerBoxCents) * 100));
    }

    /**
     * Preços finais dinâmicos a partir do OTP/sub por box do Recharge (ou variant price).
     * Preço riscado = NORMAL_COST mockado × pack size.
     */
    _getDynamicPricing(packSize = this.packSize) {
      const otpPerBoxCents = this._getOtpPerBoxCents();
      if (!otpPerBoxCents) return null;

      const otpPackPct = this._packSavePctForSize(packSize, false);
      const subPackPct = this._packSavePctForSize(packSize, true);

      const otpPerBoxAfterPackCents =
        otpPerBoxCents - Math.floor((otpPerBoxCents * otpPackPct) / 100);
      const subPerBoxCents =
        otpPerBoxCents - Math.floor((otpPerBoxCents * subPackPct) / 100);

      return {
        regularUsd: NORMAL_COST_PER_BOX_USD * packSize,
        subFinalUsd: (subPerBoxCents * packSize) / 100,
        otpFinalUsd: (otpPerBoxAfterPackCents * packSize) / 100,
        packSize,
        otpPackPct,
        subPackPct
      };
    }

    _priceFormatRef() {
      return (
        this._rcBaseOtp ||
        this._rcBaseSub || {
          prefix: '$',
          suffix: '',
          value: (this._getOtpPerBoxCents() || 0) / 100 || NORMAL_COST_PER_BOX_USD
        }
      );
    }

    _packSavePctForSize(packSize, subMode) {
      const map = subMode ? PACK_SAVE_PCT.subscription : PACK_SAVE_PCT.onetime;
      return map[packSize] ?? 0;
    }

    _saveTextForPack(packSize, subMode) {
      const pct = this._packSavePctForSize(packSize, subMode);
      return pct > 0 ? `Save ${pct}%` : null;
    }

    _badgeForPack(packSize, subMode) {
      const map = subMode ? PACK_BADGE.subscription : PACK_BADGE.onetime;
      return map[packSize] || null;
    }

    _refreshPackBadges() {
      const subMode = this._getPurchaseMode() === 'subscription';

      // On the mint PDP, badges come from the template's block settings
      // (e.g. "MOST POPULAR" / "BEST VALUE") instead of the discount-based
      // PACK_BADGE map, so they don't get overwritten on every refresh.
      const useSettingsBadges = this.dataset.templateSuffix === 'mint';

      this.packButtons.forEach((btn) => {
        // The B1G1 tile renders its own banner/price from Liquid — never overwrite it.
        if (btn.dataset.packFreeGift) return;
        const packSize = parseInt(btn.dataset.packSize, 10) || 1;
        const cfg = useSettingsBadges
          ? (btn.dataset.packBadge
              ? { text: btn.dataset.packBadge, color: btn.dataset.packBadgeColor || '#bc1218' }
              : null)
          : this._badgeForPack(packSize, subMode);

        if (!cfg) {
          btn.querySelector('.cpfp__pack-badge')?.remove();
          return;
        }

        let badgeEl = btn.querySelector('.cpfp__pack-badge');
        if (!badgeEl) {
          badgeEl = document.createElement('span');
          badgeEl.className = 'cpfp__pack-badge';
          badgeEl.setAttribute('aria-hidden', 'true');
          btn.insertBefore(badgeEl, btn.firstChild);
        }

        badgeEl.textContent = cfg.text;
        badgeEl.style.backgroundColor = cfg.color;
      });
    }

    /**
     * Preço por barra do tile, dependente do modo de compra.
     * `basePrice` (vindo do Liquid) é o preço por barra de ONE-TIME do pack:
     * em one-time exibe como está; em subscription reescala pela razão dos
     * descontos do pack (sub vs otp), preservando prefixo/sufixo (ex.: "$…/bar").
     */
    _perBarLineForMode(basePrice, packSize, subMode) {
      if (!basePrice || !subMode) return basePrice;
      const parsed = this._parsePrice(basePrice);
      if (!parsed) return basePrice;
      const otpFactor = (100 - this._packSavePctForSize(packSize, false)) / 100;
      const subFactor = (100 - this._packSavePctForSize(packSize, true)) / 100;
      if (otpFactor <= 0) return basePrice;
      return this._formatPrice(parsed, parsed.value * (subFactor / otpFactor));
    }

    _refreshPackSaveLabels() {
      const mode = this._getPurchaseMode();
      const subMode = mode === 'subscription';

      this.packButtons.forEach((btn) => {
        // The B1G1 tile renders its own price/free-shipping line from Liquid.
        if (btn.dataset.packFreeGift) return;
        const priceEl = btn.querySelector('.cpfp__pack-price');
        if (!priceEl) return;

        const packSize = parseInt(btn.dataset.packSize, 10) || 1;
        let basePrice = btn.dataset.packPriceText || '';
        if (!basePrice) {
          const clone = priceEl.cloneNode(true);
          clone.querySelector('.cpfp__pack-save-sep')?.remove();
          clone.querySelector('.cpfp__pack-save')?.remove();
          basePrice = clone.textContent.trim();
          if (basePrice) btn.dataset.packPriceText = basePrice;
        }

        const saveText = this._saveTextForPack(packSize, subMode);
        const priceLine = this._perBarLineForMode(basePrice, packSize, subMode);

        priceEl.innerHTML =
          priceLine +
          (saveText
            ? `<span class="cpfp__pack-save-sep" aria-hidden="true"> · </span><span class="cpfp__pack-save">${saveText}</span>`
            : '');

        btn.dataset.cpfpSavePct = saveText ? String(this._packSavePctForSize(packSize, subMode)) : '';
      });

      this.dataset.cpfpPurchaseMode = mode;
    }

    _syncPackPricing() {
      // Swap the B1G1 tile between its Subscribe and One-time treatments.
      if (this.b1g1) {
        this.classList.toggle('is-onetime', this._getPurchaseMode() !== 'subscription');
      }
      this._refreshPackBadges();
      this._refreshPackSaveLabels();
      this._updateRechargeBundlePrice();
      // React to Subscribe ↔ One-time toggles (the free gift is sub-only).
      this._syncFreeGiftMode();
    }

    _packDiscountPctForSize(packSize) {
      const subMode = this._getPurchaseMode() === 'subscription';
      return this._packSavePctForSize(packSize, subMode);
    }

    _currentPackDiscountPct() {
      return this._packDiscountPctForSize(this.packSize);
    }

    _isRechargePurchaseTarget(el) {
      if (!el || typeof el !== 'object') return false;
      if (el.tagName === 'INPUT') {
        const name = el.name || '';
        if (name === 'purchaseOption' || name === 'purchase_option') return true;
      }
      const part = el.getAttribute?.('part') || '';
      if (part.includes('rc-purchase-option')) return true;
      const cls = el.classList;
      if (!cls) return false;
      return (
        cls.contains('rc-purchase-option__subscription') ||
        cls.contains('rc-purchase-option__onetime') ||
        cls.contains('rc-purchase-option__label') ||
        cls.contains('rc-selection__subscription') ||
        cls.contains('rc-selection__onetime')
      );
    }

    _schedulePriceRefresh() {
      const run = () => {
        if (this.packSize <= 1) this._storeRechargeBasePrices();
        this._syncPackPricing();
      };
      run();
      requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
      [16, 40, 80, 130, 200, 280, 400, 550, 800, 1200].forEach((ms) => {
        setTimeout(run, ms);
      });
    }

    _getRechargeShadowRoot() {
      const w = document.querySelector('recharge-subscription-widget');
      return w?.shadowRoot || null;
    }

    /** Subscribe vs one-time — shadow DOM first (Recharge v2), form as fallback. */
    _getPurchaseMode() {
      const sr = this._getRechargeShadowRoot();

      if (sr) {
        const po =
          sr.querySelector('input[name="purchaseOption"]:checked') ||
          sr.querySelector('input[name="purchase_option"]:checked');
        if (po?.value) {
          const pv = String(po.value).toLowerCase();
          if (
            pv.includes('sub') ||
            pv === 'subscription' ||
            pv.includes('auto') ||
            pv.includes('deliver')
          ) {
            return 'subscription';
          }
          if (
            pv.includes('one') ||
            pv === 'otp' ||
            pv === 'onetime' ||
            pv === 'one_time'
          ) {
            return 'onetime';
          }
        }

        const checkedRadios = sr.querySelectorAll('input[type="radio"]:checked');
        for (let i = 0; i < checkedRadios.length; i++) {
          const host = checkedRadios[i].closest(
            '.rc-purchase-option__subscription, .rc-purchase-option__onetime, .rc-selection__subscription, .rc-selection__onetime'
          );
          if (!host) continue;
          if (
            host.classList.contains('rc-purchase-option__subscription') ||
            host.classList.contains('rc-selection__subscription')
          ) {
            return 'subscription';
          }
          if (
            host.classList.contains('rc-purchase-option__onetime') ||
            host.classList.contains('rc-selection__onetime')
          ) {
            return 'onetime';
          }
        }

        const selectedPart = sr.querySelector('[part*="rc-purchase-option__selected"]');
        if (selectedPart) {
          const partStr = (selectedPart.getAttribute('part') || '').toLowerCase();
          if (partStr.includes('subscription') || partStr.includes('subscribe')) {
            return 'subscription';
          }
          if (
            partStr.includes('onetime') ||
            partStr.includes('one_time') ||
            partStr.includes('otp')
          ) {
            return 'onetime';
          }
          const inSub = selectedPart.closest(
            '.rc-purchase-option__subscription, .rc-selection__subscription'
          );
          const inOt = selectedPart.closest(
            '.rc-purchase-option__onetime, .rc-selection__onetime'
          );
          if (inSub && !inOt) return 'subscription';
          if (inOt && !inSub) return 'onetime';
        }

        const cards = sr.querySelectorAll(
          '.rc-purchase-option__subscription, .rc-purchase-option__onetime, .rc-selection__subscription, .rc-selection__onetime'
        );
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i];
          const part = card.getAttribute('part') || '';
          if (part.includes('__selected')) {
            if (
              card.classList.contains('rc-purchase-option__subscription') ||
              card.classList.contains('rc-selection__subscription')
            ) {
              return 'subscription';
            }
            if (
              card.classList.contains('rc-purchase-option__onetime') ||
              card.classList.contains('rc-selection__onetime')
            ) {
              return 'onetime';
            }
          }
        }
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i];
          if (card.querySelector?.('[part*="__selected"]')) {
            if (
              card.classList.contains('rc-purchase-option__subscription') ||
              card.classList.contains('rc-selection__subscription')
            ) {
              return 'subscription';
            }
            if (
              card.classList.contains('rc-purchase-option__onetime') ||
              card.classList.contains('rc-selection__onetime')
            ) {
              return 'onetime';
            }
          }
        }

        if (
          sr.querySelector(
            'input[type="radio"][value="subscription"]:checked, input[name="purchase_option"][value="subscription"]:checked'
          )
        ) {
          return 'subscription';
        }
        if (
          sr.querySelector(
            'input[type="radio"][value="onetime"]:checked, input[type="radio"][value="otp"]:checked'
          )
        ) {
          return 'onetime';
        }
      }

      const otpChecked =
        this._deepQuery('input[name="purchaseOption"][value="onetime"]:checked') ||
        this._deepQuery('input[name="purchase_option"][value="onetime"]:checked') ||
        this._deepQuery('input[name="purchaseOption"][value="otp"]:checked');
      const subChecked =
        this._deepQuery('input[name="purchaseOption"][value="subscription"]:checked') ||
        this._deepQuery('input[name="purchase_option"][value="subscription"]:checked');

      if (otpChecked && !subChecked) return 'onetime';
      if (subChecked && !otpChecked) return 'subscription';

      const form = this.productFormEl;
      if (form) {
        if (form.querySelector('input[name="purchaseOption"][value="subscription"]:checked')) {
          return 'subscription';
        }
        if (form.querySelector('input[name="purchase_type"][value="subscription"]:checked')) {
          return 'subscription';
        }
        if (form.querySelector('input[name="purchaseOption"][value="onetime"]:checked')) {
          return 'onetime';
        }
      }

      return 'subscription';
    }

    _barsPerBox() {
      const sub = this.querySelector('.cpfp__label-sub');
      const raw = sub?.textContent || '';
      const m = raw.match(/(\d+)\s*bars?\s*per\s*box/i);
      return m ? parseInt(m[1], 10) || 7 : 7;
    }

    _readDisplayedPriceFromLabel(label, kind) {
      if (!label) return null;
      const overrideFinal = label.querySelector('[data-cpfp-rc] span:last-child');
      if (overrideFinal) return this._parsePrice(overrideFinal.textContent);
      return this._readPriceFromLabel(label, kind);
    }

    _storeRechargeBasePrices() {
      // Normally the native per-box base is only captured in 1-box mode. On a
      // B1G1 template the page can load in multi-pack mode, so we must also be
      // allowed to snapshot the native price there (before our first override).
      // The cpfp-rc-overridden guards below still prevent capturing our own
      // overridden values. Live (non-b1g1) templates keep the original gate.
      if (this.packSize > 1 && !this.b1g1) return false;
      const { subLabel, otpLabel } = this._findRechargeOptions();
      if (!subLabel && !otpLabel) return false;
      if (subLabel?.classList.contains('cpfp-rc-overridden')) return false;
      if (otpLabel?.classList.contains('cpfp-rc-overridden')) return false;

      const sub = this._readPriceFromLabel(subLabel, 'sub');
      const otp = this._readPriceFromLabel(otpLabel, 'otp');
      if (sub) this._rcBaseSub = sub;
      if (otp) this._rcBaseOtp = otp;
      return true;
    }

    _updateRechargeBundlePrice() {
      const { subLabel, otpLabel } = this._findRechargeOptions();
      if (!subLabel && !otpLabel) return;

      if (this.packSize <= 1) {
        this._storeRechargeBasePrices();
      } else if (this.b1g1 && (this._rcBaseSub == null || this._rcBaseOtp == null)) {
        // B1G1 loads in multi-pack mode: capture the native base from the
        // not-yet-overridden labels before we overwrite them, so switching to
        // 1/2 box later reads the real per-box price (not the bundle total).
        this._storeRechargeBasePrices();
      }

      const ref = this._priceFormatRef();
      const pricing = this._getDynamicPricing(this.packSize);
      if (!pricing) return;

      const { regularUsd, subFinalUsd, otpFinalUsd, otpPackPct } = pricing;
      const showStrikeSub = regularUsd !== subFinalUsd;

      // Subscribe = 5 paid boxes (1 free), priced by the engine above.
      // One-time = 6 paid boxes at a fixed total (must match the discount app),
      // struck through at the regular price.
      let otpRegular = regularUsd;
      let otpFinal = otpFinalUsd;
      let showStrikeOtp = otpPackPct > 0 && regularUsd !== otpFinalUsd;
      if (this.b1g1 && this.currentPackFreeGift && this.onetimeTotal > 0) {
        otpFinal = this.onetimeTotal;
        otpRegular =
          this.onetimeCompare > 0
            ? this.onetimeCompare
            : NORMAL_COST_PER_BOX_USD * this._effectiveTotal();
        showStrikeOtp = true;
      }

      this._rcUpdatingFromUs = true;
      try {
        if (subLabel) {
          this._applyRcOverride(
            subLabel,
            regularUsd,
            subFinalUsd,
            ref,
            showStrikeSub
          );
        }
        if (otpLabel) {
          if (showStrikeOtp) {
            this._applyRcOverride(otpLabel, otpRegular, otpFinal, ref, true);
          } else {
            this._clearRcOverride(otpLabel);
          }
        }
      } finally {
        requestAnimationFrame(() => {
          this._rcUpdatingFromUs = false;
        });
      }
    }

    _applyRcOverride(label, regularValue, finalValue, refParsed, showStrikethrough) {
      const pricesEl = label.querySelector('.rc-purchase-option__prices');
      if (!pricesEl) return;

      // Inline styles — required because the widget lives inside a shadow root,
      // so external CSS (custom-pack-flavor-picker.css) cannot reach it.
      const STYLE_WRAP =
        'display:inline-flex;align-items:baseline;gap:8px;' +
        'font-variant-numeric:tabular-nums;white-space:nowrap;';
      const STYLE_REGULAR =
        'color:#8a8a8a;text-decoration:line-through;font-weight:500;';
      const STYLE_FINAL_DISCOUNT = 'color:#1ea84b;font-weight:700;';
      const STYLE_FINAL_PLAIN = 'font-weight:inherit;';

      let wrap = pricesEl.querySelector('[data-cpfp-rc]');
      if (!wrap) {
        wrap = document.createElement('span');
        wrap.setAttribute('data-cpfp-rc', '');
        pricesEl.appendChild(wrap);
      }
      wrap.style.cssText = STYLE_WRAP;

      const regularStr = this._formatPrice(refParsed, regularValue);
      const finalStr = this._formatPrice(refParsed, finalValue);
      const showStrike = showStrikethrough && regularValue !== finalValue;

      wrap.innerHTML = showStrike
        ? '<span style="' +
          STYLE_REGULAR +
          '">' +
          regularStr +
          '</span>' +
          '<span style="' +
          STYLE_FINAL_DISCOUNT +
          '">' +
          finalStr +
          '</span>'
        : '<span style="' + STYLE_FINAL_PLAIN + '">' + finalStr + '</span>';

      Array.from(pricesEl.querySelectorAll('.rc-price')).forEach((el) => {
        if (el.closest('[data-cpfp-rc]')) return;
        el.setAttribute('data-cpfp-rc-hidden', '');
        el.style.display = 'none';
      });
      label.classList.add('cpfp-rc-overridden');
    }

    _clearRcOverride(label) {
      if (!label) return;
      const pricesEl = label.querySelector('.rc-purchase-option__prices');
      if (!pricesEl) return;
      pricesEl.querySelector('[data-cpfp-rc]')?.remove();
      pricesEl.querySelectorAll('.rc-price[data-cpfp-rc-hidden]').forEach((el) => {
        el.style.removeProperty('display');
        el.removeAttribute('data-cpfp-rc-hidden');
      });
      label.classList.remove('cpfp-rc-overridden');
    }

    /* ------------------------------------------------------------------
     * Section refresh on flavor change (1-box mode only)
     *
     * Each flavor is a different PRODUCT, so we cannot use Shopify's
     * `?section_id=…` API (section IDs are scoped to a single product). We
     * fetch the full product page and surgically extract & swap the
     * `<product-gallery>` so the gallery reflects the newly selected flavor.
     * The custom picker DOM is preserved (we don't replace the whole
     * section) so user state stays intact.
     * ----------------------------------------------------------------*/
    _refreshSectionForFlavor(row) {
      if (this.packSize !== 1) return;
      const productUrl = row?.dataset?.productUrl;
      if (!productUrl) return;

      // Skip when the selected flavor IS the current PDP product (gallery is
      // already correct, no need to re-fetch).
      const handle = row.dataset.productHandle || '';
      if (handle && this.currentProductHandle && handle === this.currentProductHandle) {
        return;
      }

      // Cancel any previous in-flight fetch.
      if (this._galleryAbortController) {
        try { this._galleryAbortController.abort(); } catch (_) {}
      }
      this._galleryAbortController = new AbortController();
      const signal = this._galleryAbortController.signal;

      // Claim the pending modal handle so the sibling reconcile() call that
      // runs synchronously after this one skips kicking off its own fetch.
      this._pendingModalHandle = handle;

      // Start exit animation on the current gallery (fades + slides down).
      const currentGallery = this._findProductGallery(document);
      if (currentGallery) this._setGalleryAnimState(currentGallery, 'leaving');

      // Hold the swap for at least the exit duration, even if the fetch
      // resolves from cache instantly. Keeps the transition feel consistent.
      const EXIT_MS = 220;
      const exitTimer = new Promise((resolve) => setTimeout(resolve, EXIT_MS));

      this._sectionHtmlCache = this._sectionHtmlCache || new Map();
      const cacheKey = productUrl;
      const cached = this._sectionHtmlCache.get(cacheKey);

      const htmlPromise = cached
        ? Promise.resolve(cached)
        : fetch(productUrl, { signal, headers: { Accept: 'text/html' } })
            .then((res) =>
              res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))
            )
            .then((html) => {
              if (this._sectionHtmlCache.size > 24) {
                const firstKey = this._sectionHtmlCache.keys().next().value;
                this._sectionHtmlCache.delete(firstKey);
              }
              this._sectionHtmlCache.set(cacheKey, html);
              return html;
            });

      Promise.all([htmlPromise, exitTimer])
        .then(([html]) => {
          if (signal.aborted) return;
          this._applySectionHtml(html, { modalHandle: handle });
        })
        .catch((err) => {
          if (currentGallery && document.contains(currentGallery)) {
            this._setGalleryAnimState(currentGallery, 'visible');
          }
          if (this._pendingModalHandle === handle) {
            this._pendingModalHandle = null;
          }
          if (err?.name === 'AbortError') return;
          console.warn('[custom-pack-flavor-picker] section refresh failed', err);
        });
    }

    _applySectionHtml(html, options = {}) {
      // Use DOMParser so the parsed tree is inert (no scripts run, no images
      // get fetched) until we move nodes into the live document.
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const oldGallery = this._findProductGallery(document);
      const newGallery = this._findProductGallery(doc);

      if (oldGallery && newGallery) {
        // Adopt the node into the live document so custom-element upgrades
        // happen against the page's registry, then swap.
        const adopted = document.adoptNode(newGallery);
        // Insert the new gallery already in the "enter-init" state (off-screen
        // below + transparent, NO transition). Then on next frame switch to
        // 'entering' so it slides up + fades in smoothly.
        this._setGalleryAnimState(adopted, 'enter-init');
        oldGallery.replaceWith(adopted);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._setGalleryAnimState(adopted, 'entering');
            setTimeout(
              () => this._setGalleryAnimState(adopted, 'visible'),
              280
            );
          });
        });
      }

      // Piggyback on the same fetched HTML to swap the nutrition modal
      // (only meaningful in 1-pack mode — multi-pack is handled separately).
      if (this.packSize === 1) {
        this._swapNutritionModalFromDoc(doc);
        if (options.modalHandle) {
          this._modalHandle = options.modalHandle;
          if (this._pendingModalHandle === options.modalHandle) {
            this._pendingModalHandle = null;
          }
        }
      }

      // Let other listeners (price, badges, etc. registered on the product
      // form) react to the new content too.
      const form = this.productFormEl;
      if (form) {
        // Build a fragment containing all top-level body children so existing
        // listeners (which use `deepQuerySelector(htmlFragment, …)`) keep
        // working transparently.
        const fragment = document.createDocumentFragment();
        Array.from(doc.body.children).forEach((node) => {
          fragment.appendChild(node.cloneNode(true));
        });
        form.dispatchEvent(
          new CustomEvent('product:rerender', {
            detail: { htmlFragment: fragment, productChange: true }
          })
        );
      }
    }

    /**
     * Directional crossfade for `<product-gallery>` swap.
     * States:
     *   - 'leaving'    → fades out + slides down (exit)
     *   - 'enter-init' → instantly positioned below + transparent (no transition)
     *   - 'entering'   → fades in + slides up to its place
     *   - 'visible'    → strips all inline styles (back to native)
     */
    _setGalleryAnimState(gallery, state) {
      if (!gallery) return;
      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        gallery.style.transition = '';
        gallery.style.opacity = '';
        gallery.style.transform = '';
        gallery.style.willChange = '';
        gallery.style.pointerEvents = '';
        return;
      }

      const OFFSET = '16px';

      switch (state) {
        case 'leaving':
          gallery.classList.add('cpfp-gallery--leaving');
          gallery.style.transition =
            'opacity 220ms cubic-bezier(0.4, 0, 0.2, 1), transform 220ms cubic-bezier(0.4, 0, 0.2, 1)';
          gallery.style.opacity = '0';
          gallery.style.transform = `translate3d(0, ${OFFSET}, 0)`;
          gallery.style.willChange = 'opacity, transform';
          gallery.style.pointerEvents = 'none';
          break;

        case 'enter-init':
          gallery.classList.remove('cpfp-gallery--leaving');
          gallery.classList.add('cpfp-gallery--entering');
          gallery.style.transition = 'none';
          gallery.style.opacity = '0';
          gallery.style.transform = `translate3d(0, ${OFFSET}, 0)`;
          gallery.style.willChange = 'opacity, transform';
          gallery.style.pointerEvents = 'none';
          break;

        case 'entering':
          gallery.style.transition =
            'opacity 280ms cubic-bezier(0.2, 0.7, 0.2, 1), transform 280ms cubic-bezier(0.2, 0.7, 0.2, 1)';
          gallery.style.opacity = '1';
          gallery.style.transform = 'translate3d(0, 0, 0)';
          break;

        case 'visible':
        default:
          gallery.classList.remove('cpfp-gallery--leaving', 'cpfp-gallery--entering');
          gallery.style.transition = '';
          gallery.style.opacity = '';
          gallery.style.transform = '';
          gallery.style.willChange = '';
          gallery.style.pointerEvents = '';
          break;
      }
    }

    _findProductGallery(root) {
      const search = root.querySelector ? root : root.documentElement || document;
      const formId = this.productFormEl?.id;
      if (formId) {
        const scoped =
          search.querySelector(`product-gallery[form="${formId}"]`) ||
          search.querySelector(`product-gallery[form-id="${formId}"]`);
        if (scoped) return scoped;
      }
      return search.querySelector('product-gallery');
    }

    /* ------------------------------------------------------------------
     * Nutrition modal reconciliation
     *
     * Rule: in 1-pack mode the modal shows the selected flavor's nutrition;
     * in multi-pack mode it shows the Variety Pack swiper (all flavors).
     * Modal content is fetched from the relevant product page and the
     * existing #nutritionModalTrigger + #nutritionModal nodes are replaced.
     * The original modal is snapshotted at startup so returning to the
     * page's PDP product is free.
     * ----------------------------------------------------------------*/
    _reconcileNutritionModal() {
      let targetHandle = '';
      let targetUrl = '';

      if (this.packSize > 1) {
        targetHandle = this.varietyPackHandle;
        targetUrl = this.varietyPackUrl;
      } else {
        const row = this.selectedFlavorId
          ? this.querySelector(
              `[data-select-row="${CSS.escape(this.selectedFlavorId)}"]`
            )
          : null;
        if (!row) return;
        targetHandle = row.dataset.productHandle || '';
        targetUrl = row.dataset.productUrl || '';
      }

      if (!targetHandle) return;
      if (this._modalHandle === targetHandle) return;
      if (this._pendingModalHandle === targetHandle) return;

      this._pendingModalHandle = targetHandle;

      if (targetHandle === this.currentProductHandle && this._initialModalSnapshot) {
        this._applyModalSnapshot(this._initialModalSnapshot);
        this._modalHandle = targetHandle;
        this._pendingModalHandle = null;
        return;
      }

      if (!targetUrl) {
        this._pendingModalHandle = null;
        return;
      }

      this._fetchAndSwapModal(targetUrl, targetHandle);
    }

    _fetchAndSwapModal(url, handle) {
      this._sectionHtmlCache = this._sectionHtmlCache || new Map();
      const cached = this._sectionHtmlCache.get(url);
      const htmlPromise = cached
        ? Promise.resolve(cached)
        : fetch(url, { headers: { Accept: 'text/html' } })
            .then((res) =>
              res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))
            )
            .then((html) => {
              if (this._sectionHtmlCache.size > 24) {
                const firstKey = this._sectionHtmlCache.keys().next().value;
                this._sectionHtmlCache.delete(firstKey);
              }
              this._sectionHtmlCache.set(url, html);
              return html;
            });

      htmlPromise
        .then((html) => {
          if (this._pendingModalHandle !== handle) return;
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          this._swapNutritionModalFromDoc(doc);
          this._modalHandle = handle;
          this._pendingModalHandle = null;
        })
        .catch((err) => {
          if (this._pendingModalHandle === handle) this._pendingModalHandle = null;
          if (err?.name === 'AbortError') return;
          console.warn('[custom-pack-flavor-picker] modal fetch failed', err);
        });
    }

    _swapNutritionModalFromDoc(doc) {
      const newTrigger = doc.getElementById('nutritionModalTrigger');
      const newModal = doc.getElementById('nutritionModal');
      this._swapNutritionModalNodes(newTrigger, newModal);
    }

    _applyModalSnapshot(snap) {
      const tpl = document.createElement('template');
      tpl.innerHTML = snap.triggerHtml + snap.modalHtml;
      this._swapNutritionModalNodes(
        tpl.content.querySelector('#nutritionModalTrigger'),
        tpl.content.querySelector('#nutritionModal')
      );
    }

    _swapNutritionModalNodes(newTrigger, newModal) {
      const liveTrigger = document.getElementById('nutritionModalTrigger');
      const liveModal = document.getElementById('nutritionModal');

      if (newTrigger && liveTrigger) {
        const adopted =
          newTrigger.ownerDocument === document
            ? newTrigger
            : document.adoptNode(newTrigger);
        liveTrigger.replaceWith(adopted);
      }

      if (newModal && liveModal) {
        const wasOpen = liveModal.classList.contains('active');
        const adopted =
          newModal.ownerDocument === document
            ? newModal
            : document.adoptNode(newModal);
        if (wasOpen) {
          adopted.classList.add('active');
          // The trigger node was already replaced above; sync its aria.
          const liveAfter = document.getElementById('nutritionModalTrigger');
          if (liveAfter) liveAfter.setAttribute('aria-expanded', 'true');
        }
        liveModal.replaceWith(adopted);
      }
    }

    _resetSelection() {
      this.quantities.clear();
      if (this.packSize === 1) {
        if (!this._applyDefaultSingleFlavor()) {
          this.selectedFlavorId = '';
          if (this.selectName) this.selectName.textContent = this.placeholderText;
          this._updateSelectThumb(null);
          this.selectRows.forEach((r) => r.setAttribute('aria-selected', 'false'));
          this._closeSelect();
          this._syncProductTitleDisplay('');
        }
      } else {
        this.flavorRows.forEach((row) => this._setRowQty(row, 0));
        if (this.freeGiftActive) this._prefillFreeGiftBundle();
        this._refreshStepperDisabledState();
        this._refreshBuilderUi();
      }
      this._updateSubmitState();
    }
  }

  customElements.define('custom-pack-flavor-picker', CustomPackFlavorPicker);
})();
